'use client';

import { InfoCircle } from '@shared/icons';
import { ROUTES } from '@shared/utils';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { usePathname, useSearchParams } from 'next/navigation';
import { Suspense, useEffect, useEffectEvent, useRef, useState, type ReactNode } from 'react';
import { PageToolbarWithNavigation } from '../../PageToolbarWithNavigation/PageToolbarWithNavigation';
import { SPORTSBOOK_URL_CHANGE_EVENT } from '../utils/sportsbookUrl';

type SportsbookLayoutShellProps = {
  children: ReactNode;
};

const { HELP_GUIDE, ROOT_SPORTSBOOK_PATHNAME } = ROUTES.SPORTS;
const NAMESPACE = 'sportsbook.subnavigation';
const SUBNAV_PAGES = new Set(['overview', 'live', 'betHistory', 'allSports']);

const getPageFromSearch = (search: string) => new URLSearchParams(search).get('page') || 'overview';

const getSportsbookToolbarPath = (pathname: string, page: string) => {
  if (pathname !== ROOT_SPORTSBOOK_PATHNAME) return pathname;
  return `${pathname}?page=${page}`;
};

type SportsbookToolbarProps = {
  nextPage: string;
  onReloadWidget: () => void;
};

function SportsbookToolbar({ nextPage, onReloadWidget }: SportsbookToolbarProps) {
  const t = useTranslations(NAMESPACE);
  const pathname = usePathname();
  const [inMemoryPage, setInMemoryPage] = useState<string | null>(null);
  const hasSyncedNextPage = useRef(false);
  const lastSubnavPageRef = useRef(SUBNAV_PAGES.has(nextPage) ? nextPage : 'overview');

  const syncInMemoryPage = useEffectEvent(() => {
    setInMemoryPage(getPageFromSearch(window.location.search));
  });

  useEffect(() => {
    if (!hasSyncedNextPage.current) {
      hasSyncedNextPage.current = true;
      return;
    }
    setInMemoryPage(null);
  }, [pathname, nextPage]);

  useEffect(() => {
    window.addEventListener(SPORTSBOOK_URL_CHANGE_EVENT, syncInMemoryPage);
    window.addEventListener('popstate', syncInMemoryPage);

    return () => {
      window.removeEventListener(SPORTSBOOK_URL_CHANGE_EVENT, syncInMemoryPage);
      window.removeEventListener('popstate', syncInMemoryPage);
    };
  }, []);

  const resolvedPage = inMemoryPage ?? nextPage;

  if (SUBNAV_PAGES.has(resolvedPage)) {
    lastSubnavPageRef.current = resolvedPage;
  }

  const currentPage = SUBNAV_PAGES.has(resolvedPage) ? resolvedPage : lastSubnavPageRef.current;
  const toolbarPath = getSportsbookToolbarPath(pathname, currentPage);

  function handleSamePageClick(e: React.MouseEvent<HTMLAnchorElement>) {
    const href = e.currentTarget.href;
    const url = new URL(href, window.location.origin);
    const page = url.searchParams.get('page');
    if (page === currentPage) {
      onReloadWidget();
    }
  }

  function handleHelpGuideClick(event: React.MouseEvent<HTMLAnchorElement>) {
    event.preventDefault();
    window.dispatchEvent(new CustomEvent('legacy:open', { detail: { href: HELP_GUIDE } }));
  }

  return (
    <PageToolbarWithNavigation namespace={NAMESPACE} productVertical="sports" path={toolbarPath} data-track-sub-navigation-title="Sports" onItemClick={handleSamePageClick}>
      <Link
        href={HELP_GUIDE}
        prefetch={false}
        data-testid="navigation-toolbar-help-guide"
        onClick={handleHelpGuideClick}
        className="paragraph-s-strong absolute top-1 right-5 lg:right-auto lg:left-39 lg:top-5 flex items-center gap-1 z-10"
      >
        {t('help_guide')} <InfoCircle size="small" />
      </Link>
    </PageToolbarWithNavigation>
  );
}

// Reads the query string, which forces a client-side render of this subtree on prerendered pages. Keeping it in its own
// tiny Suspense boundary (and not around `children`) leaves the page content server-rendered.
function SportsbookToolbarWithSearchParams({ onReloadWidget }: Pick<SportsbookToolbarProps, 'onReloadWidget'>) {
  const nextPage = useSearchParams().get('page') || 'overview';

  return <SportsbookToolbar nextPage={nextPage} onReloadWidget={onReloadWidget} />;
}

function SportsbookLayoutShell({ children }: SportsbookLayoutShellProps) {
  const [widgetReloadKey, setWidgetReloadKey] = useState(0);
  const reloadWidget = () => setWidgetReloadKey(prev => prev + 1);

  return (
    <>
      <Suspense fallback={<SportsbookToolbar nextPage="overview" onReloadWidget={reloadWidget} />}>
        <SportsbookToolbarWithSearchParams onReloadWidget={reloadWidget} />
      </Suspense>
      <div key={widgetReloadKey}>{children}</div>
    </>
  );
}

export { SportsbookLayoutShell };
